import queryString from 'query-string';

import { appConfig } from '@config';
import { ADD_DATA } from '@constants/general';
import {
  axiosInstance,
  callApi,
  deleteRequest,
  getRequest,
  handleError,
  handleResponse,
  postRequest,
  putRequest,
} from '@helpers/api';
import failErrorResponse from '@helpers/api/httpResponse';
import { setHeaders } from '@helpers/api/setHeaders';
import { buildTransactionSearchString } from '@helpers/general';

export * from './standardTemplate';

/**
 * Annual return - pull from previous return.
 * @param {string} standardTemplate - standard template id.
 * @returns {Promise<*>}
 */
export const getFilingDataFromPreviousReturn = standardTemplate =>
  postRequest(`vFile/standardTemplate/pullDataFromOtherPeriods/${standardTemplate}/`);

const uploadStandardTempReq = (formData, onUploadProgress) =>
  axiosInstance({
    method: 'POST',
    url: `${appConfig.apiUrl}/vFile/standardTemplate/upload/`,
    data: formData,
    onUploadProgress,
    headers: setHeaders({ 'Content-Type': 'multipart/form-data' }),
  });
const getStandardTemplatesRequest =
  agent =>
  ({ savedFilingPeriod }) =>
    agent.get(`vFile/standardTemplate/?saved_filing_period=${savedFilingPeriod}`);

const getStandardTemplateJobsRequest =
  agent =>
  ({ standardTemplate }) =>
    agent.get(`vFile/StandardTemplateJob/${standardTemplate}/`);

const saveManualAdjustmentsApi =
  agent =>
  ({ standardTemplate, data }) =>
    agent.post(`vFile/generateVatReturn/${standardTemplate}/`, data);

const deleteStandardTemplateRequest =
  agent =>
  ({ standardTemplate }) =>
    agent.delete(`vFile/standardTemplateEmptyData/${standardTemplate}/`);

const createStandardTemplatesRequest =
  agent =>
  ({ savedFilingPeriod }) =>
    agent.post('vFile/standardTemplate/', { saved_filing_period: savedFilingPeriod });

const getStandardTemplateAggregateRequest = agent => standardTemplate =>
  agent.get(`vFile/standardTemplateAggregate/${standardTemplate}/`);

const getStandardTemplateComplianceEcslErrorsRequest =
  agent =>
  ({ standardTemplate }) =>
    agent.get(`vFile/standardTemplateComplianceErrorECSL/${standardTemplate}/`);

const getStandardTemplateErrorsAndWarningsRequest =
  agent =>
  ({ standardTemplate, aggregateId }) =>
    agent.get(
      `vFile/standardTemplateComplianceWarningAndError/${standardTemplate}/${aggregateId}/`
    );

const ignoreAllStandardTemplateComplianceWarningsRequest = agent => standardTemplate =>
  agent.delete(`vFile/standardTemplateComplianceWarningIgnoreAll/${standardTemplate}/`);

const getStandardTemplateComplianceErrorSummaryRequest = agent => standardTemplate =>
  agent.get(`vFile/standardTemplateComplianceErrorSummary/${standardTemplate}/`);

const getStandardTemplateComplianceWarningSummaryRequest = agent => standardTemplate =>
  agent.get(`vFile/standardTemplateComplianceWarningSummary/${standardTemplate}/`);

const getStandardTemplateEntryRequest = agent => standardTemplate =>
  agent.get(`vFile/standardTemplateEntry/${standardTemplate}/`);

const searchStandardTemplateEntryRequest =
  agent =>
  ({ standardTemplate, transactionType, searchTerm }) =>
    agent.get(
      `vFile/standardTemplateEntry/${standardTemplate}/${buildTransactionSearchString(
        transactionType,
        searchTerm
      )}`
    );

const getStandardTemplateEntryErrorRequest = agent => standardTemplate =>
  agent.get(`vFile/standardTemplateEntryError/${standardTemplate}/`);

const searchStandardTemplateEntryErrorRequest =
  agent =>
  ({ standardTemplate, transactionType, searchTerm }) =>
    agent.get(
      `vFile/standardTemplateEntryError/${standardTemplate}/${buildTransactionSearchString(
        transactionType,
        searchTerm
      )}`
    );

const getStandardTemplateTransactionTypesRequest = agent => standardTemplate =>
  agent.get(`vFile/standardTemplateTransactionTypes/${standardTemplate}/`);

const getStandardTemplateLogsRequest =
  agent =>
  ({ standardTemplate, tab }) =>
    agent.get(`vFile/standardTemplateChangeLogs/${standardTemplate}/?tab=${tab}`);

const standardTemplateReProcessRequest =
  agent =>
  ({ standardTemplate }) =>
    agent.put(`vFile/standardTemplateReprocessData/${standardTemplate}/`);

const getEcslAggregatesRequest = agent => standardTemplate =>
  agent.get(`vFile/ecslEntry/${standardTemplate}/`);

const generateStandardTemplateDataRelationshipsRequest = agent => standardTemplate =>
  agent.post(`vFile/generateStandardTemplateDataRelationships/${standardTemplate}/`);

const getStandardTemplateDataRelationshipsItalyRequest = agent => standardTemplate =>
  agent.get(`vFile/standardTemplateDataRelationship/${standardTemplate}/`);

const getItalyStandardTemplateAggregateRequest = agent => standardTemplate =>
  agent.get(`vFile/standardTemplateAggregateItalyVAT/${standardTemplate}/`);

export const uploadStandardTemplate = async ({ postData, onUploadProgress }) =>
  uploadStandardTempReq(postData, onUploadProgress).then(handleResponse).catch(handleError);

export const getStandardTemplates = async ({ limit = 100, offset = 0, savedFilingPeriod }) =>
  callApi(getStandardTemplatesRequest)({ limit, offset, savedFilingPeriod })
    .then(handleResponse)
    .catch(handleError);

export const createStandardTemplate = async payload =>
  callApi(createStandardTemplatesRequest)(payload).then(handleResponse).catch(handleError);

export const downloadStandardTemplate = async (standardTemplate, bulk = false) =>
  axiosInstance({
    method: 'GET',
    url: `${appConfig.apiUrl}${
      bulk
        ? `/vFlow/bulkDownloadWorkingFiles/${standardTemplate}/`
        : `/vFile/standardTemplateDownloadExcel/${standardTemplate}/`
    }`,
    headers: setHeaders(),
  })
    .then(response => {
      if (response.status === 200) {
        return response.data;
      }

      if (response.status === 204) {
        return `The file is not available. Contact ${appConfig.supportEmail} if the problem persists.`;
      }

      return response;
    })
    .catch(failErrorResponse);

export const deleteStandardTemplate = async payload =>
  callApi(deleteStandardTemplateRequest)(payload).then(handleResponse).catch(handleError);

export const getStandardTemplateJobs = async payload =>
  callApi(getStandardTemplateJobsRequest)(payload).then(handleResponse).catch(handleError);

export const getStandardTemplateAggregate = async payload =>
  callApi(getStandardTemplateAggregateRequest)(payload).then(handleResponse).catch(handleError);

export const getStandardTemplateErrorsAndWarnings = async payload =>
  callApi(getStandardTemplateErrorsAndWarningsRequest)(payload)
    .then(handleResponse)
    .catch(handleError);

export const getStandardTemplateEcslComplianceErrors = async payload =>
  callApi(getStandardTemplateComplianceEcslErrorsRequest)(payload)
    .then(handleResponse)
    .catch(handleError);

export const ignoreAllStandardTemplateComplianceWarnings = async payload =>
  callApi(ignoreAllStandardTemplateComplianceWarningsRequest)(payload)
    .then(handleResponse)
    .catch(handleError);

export const getStandardTemplateComplianceErrorSummary = async payload =>
  callApi(getStandardTemplateComplianceErrorSummaryRequest)(payload)
    .then(handleResponse)
    .catch(handleError);

export const getStandardTemplateComplianceWarningSummary = async payload =>
  callApi(getStandardTemplateComplianceWarningSummaryRequest)(payload)
    .then(handleResponse)
    .catch(handleError);

export const getStandardTemplateEntry = async payload =>
  callApi(getStandardTemplateEntryRequest)(payload).then(handleResponse).catch(handleError);

export const searchStandardTemplateEntry = async payload =>
  callApi(searchStandardTemplateEntryRequest)(payload).then(handleResponse).catch(handleError);

export const getStandardTemplateEntryError = async payload =>
  callApi(getStandardTemplateEntryErrorRequest)(payload).then(handleResponse).catch(handleError);

export const searchStandardTemplateEntryError = async payload =>
  callApi(searchStandardTemplateEntryErrorRequest)(payload).then(handleResponse).catch(handleError);

export const getStandardTemplateTransactionTypes = async payload =>
  callApi(getStandardTemplateTransactionTypesRequest)(payload)
    .then(handleResponse)
    .catch(handleError);

export const getStandardTemplateLogs = async payload =>
  callApi(getStandardTemplateLogsRequest)(payload).then(handleResponse).catch(handleError);

export const standardTemplateReProcess = async payload =>
  callApi(standardTemplateReProcessRequest)(payload).then(handleResponse).catch(handleError);

export const getEcslAggregates = async payload =>
  callApi(getEcslAggregatesRequest)(payload).then(handleResponse).catch(handleError);

export const saveManualAdjustments = async payload =>
  callApi(saveManualAdjustmentsApi)(payload).then(handleResponse).catch(handleError);

export const generateStandardTemplateDataRelationships = async standardTemplate =>
  callApi(generateStandardTemplateDataRelationshipsRequest)(standardTemplate)
    .then(data => data)
    .catch(err => err);

export const getStandardTemplateDataRelationshipsItaly = async standardTemplate =>
  callApi(getStandardTemplateDataRelationshipsItalyRequest)(standardTemplate)
    .then(data => data)
    .catch(err => err);

export const getItalyStandardTemplateAggregate = async payload =>
  callApi(getItalyStandardTemplateAggregateRequest)(payload)
    .then(handleResponse)
    .catch(handleError);

/**
 * Fetch list of running aggregate jobs
 * @param {string} standardTemplateId Standard template ID to check
 * @returns {Promise} GET request
 */
export const checkStandardTemplateAggregateJobsRunning = standardTemplateId =>
  getRequest(`vFile/standardTemplateIsAggregateJobRunning/${standardTemplateId}/`);

/**
 * File shape
 * @typedef {Object} File
 * @param {string} id - unique identifier
 * @param {string} name - file name
 * @param {string} [date] - game changer file date
 * @param {string} [savedFilingPeriod] - saved filing period unique identifier
 * @param {string} [uploadedBy] - unique identifies user who uploaded
 * @param {string} [category] - document category
 * @param {string} [extension] - document extension
 * @param {string} [sizeDisplay] - document size
 * @param {Date} [dateCreated] - document creation date
 * @param {Date} [dateUploaded] - online spreadsheet file date uploaded
 * @param {Date} [dateModified] - online spreadsheet file date modified
 * @param {string} [standardTemplate] - standard template unique identifier
 * @param {bool} sourceHasBeenProcessed - has the source file been processed?
 */

/**
 * Process standard template data
 * @param {Object} [$0={}] - unique identifier
 * @param {string} [$0.id] - unique identifier
 * @param {Array<File>} [$0.fileList] - file list
 * @param {Array<File>} [$0.jsonList] - spreadsheet file list
 * @param {Array<File>} [$0.gcFileList] - game changer file list
 * @param {Array<*>} [$0.relationshipList] - relationship list
 * @param {string} [$0.type] - add or upload in absence of add
 * @param {boolean} [$0.isNilReturn] - is filing nil return
 * @returns {Promise<unknown | void>}
 */
export const processStandardTemplates = ({
  id,
  fileList,
  jsonList,
  gcFileList,
  relationshipList,
  type,
  isNilReturn,
} = {}) =>
  postRequest(
    `vFile/standardTemplate/uploadJobStart/${id}/`,
    isNilReturn
      ? {}
      : {
          standard_template_file_list: fileList?.filter(Boolean),
          standard_template_json_upload_list: jsonList?.filter(Boolean),
          standard_template_gc_file_list: gcFileList?.filter(Boolean),
          standard_template_st_data_rels_list:
            relationshipList?.filter(Boolean).length > 0 ? relationshipList : undefined,
        },
    {
      axiosOptions: {
        params: { adding: type === ADD_DATA ? 'true' : undefined },
        paramsSerializer: params => queryString.stringify(params),
      },
    }
  );

/**
 * Ignore warnings
 * @param {string} standardTemplate - standard template unique identifier
 * @param {string} aggregateId - aggregate unique identifier
 * @param {Array<string>} ignoreList - list of rows to be ignored
 * @param {boolean} ignoreAll - should all rows be ignored?
 * @returns {Promise<*>}
 */
export const ignoreStandardTemplateComplianceWarnings = ({
  standardTemplate,
  aggregateId,
  ignoreList,
  ignoreAll = false,
}) =>
  deleteRequest(`vFile/standardTemplateComplianceWarning/${standardTemplate}/${aggregateId}/`, {
    ignore_list: ignoreList,
    ignore_all_aggregate: ignoreAll,
  });

/**
 * Delete row
 * @param {string} standardTemplate - standard template unique identifier
 * @param {Array<string>} listRows - list of rows
 * @param {Array<string>} listErrors - list of errors
 * @returns {Promise<*>}
 */
export const deleteStandardTemplateComplianceRow = ({ standardTemplate, listRows, listErrors }) =>
  deleteRequest(`vFile/standardTemplateEditRows/${standardTemplate}/`, {
    list_rows: listRows,
    list_errors: listErrors,
  });

/**
 * Edit row
 * @param {string} standardTemplate - standard template unique identifier
 * @param {string} aggregateId - aggregate unique identifier
 * @param {Array<string>} listRows - list of rows
 * @param {Array<string>} listErrors - list of errors
 * @returns {Promise<*>}
 */
export const editStandardTemplateComplianceRow = ({
  standardTemplate,
  listRows,
  listErrors,
  aggregateId,
}) =>
  putRequest(
    aggregateId
      ? `vFile/standardTemplateEditRows/${standardTemplate}/?aggregate=${aggregateId}`
      : `vFile/standardTemplateEditRows/${standardTemplate}/`,
    {
      list_rows: listRows,
      list_errors: listErrors,
    }
  );
